import moment from 'moment';
/*
 * Copyright (c) 2017 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschuetzt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulaessig. Dies gilt
 * insbesondere fuer Vervielfaeltigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht uebergeben ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 *
 */
import TSAbstractEntity from './TSAbstractEntity';
import TSEigenleistungDaten from './TSEigenleistungDaten';
import TSEigenleistungKubikmeterDaten from './TSEigenleistungKubikmeterDaten';
import TSEigenleistungLiterDaten from './TSEigenleistungLiterDaten';
import TSEigenleistungStundenDaten from './TSEigenleistungStundenDaten';
import {TSSerializable} from './TSSerializable';

export default class TSEigenleistung extends TSAbstractEntity implements TSSerializable<TSEigenleistung> {

    private _eigenleistungDatenGS: TSEigenleistungDaten | undefined;

    private _eigenleistungDatenFS: TSEigenleistungDaten;

    private _berechtigterBetrag: number;

    private _eigenleistungStundenDaten: Array<TSEigenleistungStundenDaten> = [];

    private _eigenleistungKubikmeterDaten: TSEigenleistungKubikmeterDaten | undefined;

    private _eigenleistungLiterDaten: TSEigenleistungLiterDaten | undefined;

    private _schaetzung: boolean;

    public static getDeserializer = (eigenleistung: any) => {
        return new TSEigenleistung().deserialize(eigenleistung);
    };

    public static getSerializer = (eigenleistung: any) => {
        return eigenleistung.serialize(eigenleistung);
    };

    public get eigenleistungDatenGS(): TSEigenleistungDaten | any {
        return this._eigenleistungDatenGS;
    }

    public set eigenleistungDatenGS(value: TSEigenleistungDaten | any) {
        this._eigenleistungDatenGS = value;
    }

    public get eigenleistungDatenFS(): TSEigenleistungDaten {
        return this._eigenleistungDatenFS;
    }

    public set eigenleistungDatenFS(value: TSEigenleistungDaten) {
        this._eigenleistungDatenFS = value;
    }

    public get berechtigterBetrag(): number {
        return this._berechtigterBetrag;
    }

    public set berechtigterBetrag(value: number) {
        this._berechtigterBetrag = value;
    }

    public get eigenleistungStundenDaten(): Array<TSEigenleistungStundenDaten> {
        return this._eigenleistungStundenDaten;
    }

    public set eigenleistungStundenDaten(value: Array<TSEigenleistungStundenDaten>) {
        this._eigenleistungStundenDaten = value;
    }

    public get eigenleistungKubikmeterDaten(): TSEigenleistungKubikmeterDaten | undefined {
        return this._eigenleistungKubikmeterDaten;
    }

    public set eigenleistungKubikmeterDaten(value: TSEigenleistungKubikmeterDaten | undefined) {
        this._eigenleistungKubikmeterDaten = value;
    }

    public get eigenleistungLiterDaten(): TSEigenleistungLiterDaten | undefined {
        return this._eigenleistungLiterDaten;
    }

    public set eigenleistungLiterDaten(value: TSEigenleistungLiterDaten | undefined) {
        this._eigenleistungLiterDaten = value;
    }

    public get schaetzung(): boolean {
        return this._schaetzung;
    }

    public set schaetzung(value: boolean) {
        this._schaetzung = value;
    }

    deserialize(restObject: any): TSEigenleistung {
        super.deserialize(restObject);
        this.berechtigterBetrag = restObject.berechtigterBetrag;
        this.schaetzung = restObject.schaetzung;
        this.eigenleistungDatenFS = this.deserializeOrCreate(restObject.eigenleistungDatenFS, new TSEigenleistungDaten());
        this.eigenleistungDatenGS = this.deserializeOrNull(restObject.eigenleistungDatenGS, new TSEigenleistungDaten());
        if (restObject.eigenleistungStundenDaten) {
            this.eigenleistungStundenDaten = restObject.eigenleistungStundenDaten.map(TSEigenleistungStundenDaten.getDeserializer);
        }

        this.eigenleistungKubikmeterDaten = this.deserializeOrNull(restObject.eigenleistungKubikmeterDaten, new TSEigenleistungKubikmeterDaten());
        this.eigenleistungLiterDaten = this.deserializeOrNull(restObject.eigenleistungLiterDaten, new TSEigenleistungLiterDaten());

        return this;
    }

    serialize(restObject: any): any {
        super.serialize(restObject);
        restObject.berechtigterBetrag = this.berechtigterBetrag;
        restObject.schaetzung = this.schaetzung;
        restObject.eigenleistungDatenGS = this.serializeOrInit(this.eigenleistungDatenGS, restObject.eigenleistungDatenGS);
        restObject.eigenleistungDatenFS = this.serializeOrInit(this.eigenleistungDatenFS, restObject.eigenleistungDatenFS);
        restObject.eigenleistungStundenDaten = this.arrayToRestObject(this.eigenleistungStundenDaten);
        restObject.eigenleistungKubikmeterDaten = this.serializeOrInit(this.eigenleistungKubikmeterDaten, restObject.eigenleistungKubikmeterDaten);
        restObject.eigenleistungLiterDaten = this.serializeOrInit(this.eigenleistungLiterDaten, restObject.eigenleistungLiterDaten);
        return restObject;
    }

    public getArbeitsstundenTotal(): number {
        let total = 0;
        if (this.eigenleistungStundenDaten !== undefined) {
            for (let eigenleistungStundenDaten of this.eigenleistungStundenDaten) {
                if (eigenleistungStundenDaten && eigenleistungStundenDaten.arbeitsstunden && !eigenleistungStundenDaten.deleted) {
                    total += eigenleistungStundenDaten.arbeitsstunden;
                }
            }
        }
        return total;
    }

    public isHolz(): boolean {
        return this.eigenleistungKubikmeterDaten !== undefined;
    }

    public isTreibstoff(): boolean {
        return this.eigenleistungDatenFS.isTreibstoff();
    }

    public getMengeTotal(): number {
        let total = 0;
        if (this.eigenleistungKubikmeterDaten !== undefined && this.eigenleistungKubikmeterDaten.kubikmeter && !this.eigenleistungKubikmeterDaten.deleted) {
            total += this.eigenleistungKubikmeterDaten.kubikmeter;
        }
        return total;
    }

    public getBetragTotal(): number {
        let total = 0;
        if (this.eigenleistungStundenDaten !== undefined) {
            for (let eigenleistungStundenDaten of this.eigenleistungStundenDaten) {
                if (eigenleistungStundenDaten && eigenleistungStundenDaten.betrag && !eigenleistungStundenDaten.deleted) {
                    total += eigenleistungStundenDaten.betrag;
                }
            }
        }
        if (this.eigenleistungKubikmeterDaten !== undefined && this.eigenleistungKubikmeterDaten.betrag && !this.eigenleistungKubikmeterDaten.deleted) {
            total += this.eigenleistungKubikmeterDaten.betrag;
        }
        if (this.eigenleistungLiterDaten !== undefined && this.eigenleistungLiterDaten.betrag && !this.eigenleistungLiterDaten.deleted) {
            total += this.eigenleistungLiterDaten.betrag;
        }
        return total;
    }

    public getDateRange(): string {
        let minDate: moment.Moment | undefined;
        let maxDate: moment.Moment | undefined;

        if (this.eigenleistungStundenDaten !== undefined) {
            for (let eigenleistungStundenDaten of this.eigenleistungStundenDaten) {
                if (eigenleistungStundenDaten && eigenleistungStundenDaten.datum && !eigenleistungStundenDaten.deleted) {
                    if (minDate === undefined || minDate > eigenleistungStundenDaten.datum) {
                        minDate = eigenleistungStundenDaten.datum;
                    }
                    if (maxDate === undefined || maxDate < eigenleistungStundenDaten.datum) {
                        maxDate = eigenleistungStundenDaten.datum;
                    }
                }
            }
        }
        if (minDate && maxDate) {
            if (minDate === maxDate) {
                return minDate.format('DD.MM.YY');
            }
            return minDate.format('DD.MM.YY') + ' - ' + maxDate.format('DD.MM.YY');
        }
        return '';
    }
}



// WEBPACK FOOTER //
// ./src/app/models/TSEigenleistung.ts